import clsx from 'clsx';
import { AlertTriangle, Archive, ExternalLink, Plus, Search, Trash2 } from 'lucide-react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BILLING_TYPES, type BillingType } from '@pz/core';
import { EntryDialog, FormaIssueLink, formaKindInput, FormaKindSelect, formaLink, LocationSelects, ProjectCombobox, SourceSwitch, TaskBadges, TaskMeta, toPicked, type PickedTask, type SourceFilter } from '../components/entry';
import { Button, Checkbox, EmptyState, ErrorBox, Field, Input, Loading, Modal, Notice, PageHeader, Select, SortButton, SortTh, Textarea, th, useToast } from '../components/ui';
import { useTableSort } from '../lib/sort';
import { h } from '../lib/format';
import { useDebounced } from '../lib/hooks';
import { FavoriteStar, useFavorites } from '../components/quick';
import { cell, clip, VirtualTable } from '../components/virtual';
import { useCategories } from '../lib/categories';
import { useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';
import { rpc } from '../lib/rpc';

/** Tasks are fetched in pages of this size while the person scrolls (tens of thousands of tasks are possible). */
const PAGE = 200;

export interface EditableManualTask {
  id: string;
  ref: string;
  title: string;
  description?: string | null;
  manualCategory: string | null;
  defaultBillingType: string | null;
  billingTypeFixed: boolean;
  plannedMinutes: number | null;
  startDate: string | null;
  dueDate: string | null;
  statusGroup: string;
  /** booked hours on the task (null: not visible for the actor) */
  bookedMinutes?: number | null;
  /** project lead or office: may delete the task */
  mayDelete?: boolean;
}

/**
 * Platform task (M-number): category from the list maintained in Verwaltung > Kategorien. Planned in Projektzeit:
 * start and due date for the schedule views, planned hours (project lead), closed when done. With `task` the
 * dialog edits an existing platform task.
 */
export function ManualTaskDialog({ open, onClose, projectId: fixedProject, task }: { open: boolean; onClose: () => void; projectId?: string; task?: EditableManualTask | null }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { can, isLead } = useMe();
  const cats = useCategories(open);
  const projects = useQ('projects.list', {}, { enabled: open });
  const [projectId, setProjectId] = useState(fixedProject ?? '');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('projektarbeit');
  const [billingType, setBillingType] = useState<BillingType>('billable');
  const [fixed, setFixed] = useState(false);
  const [planHours, setPlanHours] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [closed, setClosed] = useState(false);
  const [number, setNumber] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(title.trim()), 300);
    return () => clearTimeout(id);
  }, [title]);
  useEffect(() => {
    if (open) {
      setProjectId(fixedProject ?? '');
      setTitle(task?.title ?? '');
      setCategory(task?.manualCategory ?? 'projektarbeit');
      setBillingType((task?.defaultBillingType as BillingType) ?? 'billable');
      setFixed(task?.billingTypeFixed ?? false);
      setPlanHours(task?.plannedMinutes ? String(task.plannedMinutes / 60).replace('.', ',') : '');
      setDescription(task?.description ?? '');
      setStartDate(task?.startDate ?? '');
      setDueDate(task?.dueDate ?? '');
      setClosed(!!task && task.statusGroup !== 'active');
      setNumber(task ? task.ref.replace(/^M-/, '') : '');
      setDeleting(false);
    }
  }, [open, fixedProject, task]);
  // the number of an M task: unique in the project (also against hidden tasks); checked while typing
  const oldNumber = task ? Number(task.ref.replace(/^M-/, '')) : null;
  const wanted = /^\d{1,7}$/.test(number.trim()) ? Number(number.trim()) : null;
  const numberChanged = !!task && wanted !== null && wanted !== oldNumber;
  const numberCheck = useDebounced(numberChanged ? wanted : null, 300);
  const free = useQ('tasks.numberFree', { projectId: fixedProject ?? '', number: numberCheck ?? 1, exceptId: task?.id }, { enabled: open && !!fixedProject && !!task && numberCheck !== null });
  const numberTaken = numberChanged && numberCheck === wanted && free.data?.free === false;
  const numberInvalid = !!task && wanted === null;
  const renamed = !!task && title.trim() !== task.title.trim() && title.trim().length >= 2;
  const similar = useQ('tasks.similar', { projectId, title: debounced }, { enabled: open && !task && !!projectId && debounced.length >= 4 });
  const invalidate = ['tasks.', 'projects.', 'categories.', 'analysis.', 'kpi.'];
  const create = useM('tasks.createManual', { invalidate });
  const update = useM('tasks.updateManual', { invalidate });
  const setPlan = useM('tasks.setPlan', { invalidate });
  const project = (projects.data?.projects ?? []).find((p) => p.id === projectId);
  const cat = cats.get(category);
  const internal = project?.billingMode === 'internal';
  const forced: BillingType | null = internal ? 'non_billable' : cat?.billingTypeFixed ? cat.defaultBillingType : null;
  // the billing type of a manual task: project lead, billing and office decide; others get the default of the category
  const mayBilling = !!projectId && (isLead(projectId) || can('billing.manage') || can('time.manage'));
  const shownBilling: BillingType = forced ?? (mayBilling ? billingType : ((task?.defaultBillingType as BillingType | undefined) ?? cat?.defaultBillingType ?? 'billable'));
  const billingInput = mayBilling ? { defaultBillingType: forced ?? billingType, billingTypeFixed: forced ? true : fixed } : {};
  const mayPlan = !!projectId && (can('projects.manage') || isLead(projectId));
  const selectable = (projects.data?.projects ?? []).filter((p) => !p.archived);
  const groups = [
    { key: 'projekt', label: t('categories.groups.projekt') },
    { key: 'intern', label: t('categories.groups.intern') },
  ];
  const plan = planHours.trim() ? Math.round(Number(planHours.replace(',', '.')) * 60) : null;
  const datesValid = !startDate || !dueDate || startDate <= dueDate;
  const save = async () => {
    if (task) {
      await update.mutateAsync({
        id: task.id,
        number: numberChanged && wanted !== null ? wanted : undefined,
        title,
        category,
        ...billingInput,
        description: description || null,
        startDate: startDate || null,
        dueDate: dueDate || null,
        closed,
      });
      if (mayPlan && plan !== task.plannedMinutes) await setPlan.mutateAsync({ id: task.id, plannedMinutes: plan });
      toast.ok(t('common.saved'));
      onClose();
      return;
    }
    const r = await create.mutateAsync({
      projectId,
      title,
      category,
      // without the right the server takes the default of the category (the value sent is ignored)
      defaultBillingType: forced ?? (mayBilling ? billingType : shownBilling),
      billingTypeFixed: mayBilling ? (forced ? true : fixed) : false,
      description: description || undefined,
      plannedMinutes: mayPlan ? plan : undefined,
      startDate: startDate || null,
      dueDate: dueDate || null,
    });
    toast.ok(t('tasks.created', { ref: r.ref }));
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={task ? t('tasks.editManual', { ref: task.ref }) : t('tasks.manualTitle')}
      footer={
        <>
          {task?.mayDelete && (
            <Button variant="ghost" className="mr-auto text-bad" icon={<Trash2 className="size-4" />} onClick={() => setDeleting(true)}>
              {t('tasks.delete.button')}
            </Button>
          )}
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!projectId || title.trim().length < 2 || (plan !== null && !Number.isFinite(plan)) || !datesValid || numberTaken || numberInvalid}
            loading={create.isPending || update.isPending || setPlan.isPending}
            onClick={() => save().catch(() => {})}
          >
            {task ? t('common.save') : t('common.create')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!task && <Notice tone="info">{t('tasks.manualHint')}</Notice>}
        {!fixedProject && !task && (
          <Field label={t('common.project')}>
            <ProjectCombobox
              allLabel={t('common.select')}
              value={projectId}
              onChange={setProjectId}
              projects={selectable.map((p) => ({ id: p.id, number: p.number, name: p.name, formaName: p.formaName }))}
            />
          </Field>
        )}
        <div className="flex flex-wrap items-start gap-4">
          {task && (
            <Field label={t('tasks.number')} htmlFor="mt-number" hint={t('tasks.numberHint')} error={numberTaken ? t('tasks.numberTaken', { ref: `M-${wanted}` }) : numberInvalid ? t('tasks.numberInvalid') : undefined}>
              <div className="flex items-center gap-1">
                <span className="num text-sm font-semibold text-graphite">M-</span>
                <Input id="mt-number" inputMode="numeric" className="num w-24" value={number} onChange={(e) => setNumber(e.target.value.replace(/[^0-9]/g, ''))} maxLength={7} />
              </div>
            </Field>
          )}
          <Field label={t('common.name')} className="min-w-60 flex-1">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </Field>
        </div>
        {renamed && (
          <Notice tone={task?.bookedMinutes ? 'warn' : 'neutral'}>
            {task?.bookedMinutes ? t('tasks.renameBooked', { hours: h(task.bookedMinutes) }) : t('tasks.renameHint')}
          </Notice>
        )}
        {numberChanged && !numberTaken && <p className="-mt-2 text-xs text-graphite">{t('tasks.numberChangeHint')}</p>}
        {!!similar.data?.tasks.length && (
          <Notice tone="warn">
            <div className="mb-1 font-medium">{t('tasks.similar')}</div>
            <ul className="flex flex-col gap-0.5">
              {similar.data.tasks.map((s) => (
                <li key={s.id}>
                  <span className="num font-semibold">{s.ref}</span> {s.title}
                </li>
              ))}
            </ul>
          </Notice>
        )}
        <div className="flex flex-wrap gap-4">
          <Field label={t('tasks.category')} className="min-w-56 flex-1">
            <Select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                const c = cats.get(e.target.value);
                if (c) setBillingType(c.defaultBillingType);
              }}
            >
              {groups.map((g) => (
                <optgroup key={g.key} label={g.label}>
                  {cats.list
                    .filter((c) => c.group === g.key && c.active)
                    .map((c) => (
                      <option key={c.key} value={c.key}>
                        {cats.label(c.key)}
                      </option>
                    ))}
                </optgroup>
              ))}
            </Select>
          </Field>
          <Field label={t('entry.billingType')} className="min-w-52 flex-1" hint={forced ? t('tasks.billingForced') : !mayBilling ? t('tasks.billingByRole') : undefined}>
            <Select value={shownBilling} disabled={!!forced || !mayBilling} onChange={(e) => setBillingType(e.target.value as BillingType)}>
              {BILLING_TYPES.map((b) => (
                <option key={b} value={b}>
                  {t(`billingType.${b}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {!forced && mayBilling && <Checkbox label={t('tasks.billingTypeFixed')} checked={fixed} onChange={(e) => setFixed(e.target.checked)} />}
        <div className="flex flex-wrap items-start gap-4">
          {mayPlan && (
            <Field label={t('projectKpi.planHoursLabel')} hint={t('tasks.planHint')}>
              <Input inputMode="decimal" className="num w-32" value={planHours} onChange={(e) => setPlanHours(e.target.value)} />
            </Field>
          )}
          <Field label={t('tasks.startDate')} htmlFor="mt-start">
            <Input id="mt-start" type="date" className="w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label={t('tasks.dueDate')} htmlFor="mt-due" error={!datesValid ? t('tasks.datesInvalid') : undefined}>
            <Input id="mt-due" type="date" className="w-40" value={dueDate} min={startDate || undefined} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        </div>
        <p className="-mt-2 text-xs text-graphite">{t('tasks.datesHint')}</p>
        <Field label={t('common.description')} hint={t('common.optional')}>
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        {task && <Checkbox label={t('tasks.closedLabel')} hint={t('tasks.closedHint')} checked={closed} onChange={(e) => setClosed(e.target.checked)} />}
        <ErrorBox error={create.error ?? update.error ?? setPlan.error} />
      </div>
      {task && <DeleteManualTaskDialog open={deleting} task={task} onClose={() => setDeleting(false)} onDone={onClose} />}
    </Modal>
  );
}

/**
 * Confirmation before a manual task is deleted (request 9): what the deletion removes, why hiding is the better choice,
 * and the reason. Hours of a closed month or on an invoice keep the task (then only hiding is offered).
 */
function DeleteManualTaskDialog({ open, task, onClose, onDone }: { open: boolean; task: EditableManualTask; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const usage = useQ('tasks.usage', { id: task.id }, { enabled: open, gcTime: 0, staleTime: 0 });
  const invalidate = ['tasks.', 'time.', 'projects.', 'analysis.', 'kpi.', 'approvals.', 'quick.', 'cockpit.'];
  const del = useM('tasks.deleteManual', { invalidate });
  const archive = useM('tasks.archive', { invalidate });
  const [reason, setReason] = useState('');
  const [understood, setUnderstood] = useState(false);
  useEffect(() => {
    if (open) {
      setReason('');
      setUnderstood(false);
    }
  }, [open]);
  const u = usage.data;
  const hide = () =>
    archive
      .mutateAsync({ id: task.id, archived: true })
      .then(() => {
        toast.undoable(t('tasks.delete.hidden', { ref: task.ref }), t('common.undo'), () => archive.mutateAsync({ id: task.id, archived: false }).catch(toast.error));
        onClose();
        onDone();
      })
      .catch(() => {});
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tasks.delete.title', { ref: task.ref })}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button icon={<Archive className="size-4" />} loading={archive.isPending} onClick={hide}>
            {t('tasks.delete.hideInstead')}
          </Button>
          <Button
            variant="danger"
            icon={<Trash2 className="size-4" />}
            disabled={!u?.canDelete || !understood || reason.trim().length < 3}
            loading={del.isPending}
            onClick={() =>
              del
                .mutateAsync({ id: task.id, reason: reason.trim() })
                .then((r) => {
                  toast.ok(t('tasks.delete.done', { ref: task.ref, count: r.deleted }));
                  onClose();
                  onDone();
                })
                .catch(() => {})
            }
          >
            {t('tasks.delete.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <p>
          <span className="num font-semibold text-accent">{task.ref}</span> {task.title}
        </p>
        {usage.isLoading && <Loading />}
        <ErrorBox error={usage.error} />
        {u && (
          <>
            <Notice tone="bad">
              <div className="flex gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                <div className="flex flex-col gap-1">
                  <span className="font-semibold">{t('tasks.delete.warnTitle')}</span>
                  <span>{u.entries ? t('tasks.delete.warnHours', { count: u.entries, people: u.people, hours: h(u.minutes) }) : t('tasks.delete.warnNoHours')}</span>
                  <span>{t('tasks.delete.warnFinal')}</span>
                </div>
              </div>
            </Notice>
            <p className="text-graphite">{t('tasks.delete.drafts')}</p>
            <Notice tone="info">{t('tasks.delete.better')}</Notice>
            {u.blocked > 0 && <Notice tone="warn">{t('tasks.delete.blocked', { count: u.blocked })}</Notice>}
            {u.blocked === 0 && !u.canDelete && <Notice tone="warn">{t('tasks.delete.approvedNeedsRight', { count: u.approved })}</Notice>}
            {u.canDelete && (
              <>
                <Field label={t('common.reason')} hint={t('common.reasonHint')}>
                  <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
                </Field>
                <Checkbox label={t('tasks.delete.understood')} checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
              </>
            )}
          </>
        )}
        <ErrorBox error={del.error ?? archive.error} />
      </div>
    </Modal>
  );
}

/** Tasks the person can book on, with the same filters as the booking dialog. */
export function TasksPage() {
  const { t } = useTranslation();
  const { me } = useMe();
  const cats = useCategories();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [source, setSource] = useState<SourceFilter>('all');
  const [projectId, setProjectId] = useState('');
  const [top, setTop] = useState('');
  const [sub, setSub] = useState('');
  const [category, setCategory] = useState('');
  const [kind, setKind] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [book, setBook] = useState<PickedTask | null>(null);
  const [manual, setManual] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  useEffect(() => {
    setTop('');
    setSub('');
  }, [projectId]);
  const projects = useQ('tasks.projects', {});
  const project = (projects.data?.projects ?? []).find((p) => p.id === projectId);
  const showLocations = !!project?.hasLocations && source !== 'manual';
  const locations = useQ('tasks.locations', { projectId }, { enabled: showLocations });
  // sorted by the server: the list loads page by page while scrolling (request 14 of round 4)
  const order = useTableSort<unknown>('tasks.search', { ref: () => null, task: () => null, project: () => null, billing: () => null });
  const filters = {
    ...(order.sort ? { sort: order.sort.key as 'task', dir: order.sort.dir } : {}),
    query: debounced || undefined,
    projectId: projectId || undefined,
    source: source === 'all' ? undefined : source,
    locationId: showLocations ? sub || top || undefined : undefined,
    category: source === 'manual' && category ? category : undefined,
    ...(source !== 'manual' && kind ? formaKindInput(kind) : {}),
    includeClosed,
  };
  const search = useInfiniteQuery({
    queryKey: ['tasks.searchPaged', filters],
    queryFn: ({ pageParam }) => rpc('tasks.search', { ...filters, limit: PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.more ? all.length * PAGE : undefined),
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(() => search.data?.pages.flatMap((pg) => pg.tasks) ?? [], [search.data]);
  const favs = useFavorites();
  const more = !!search.hasNextPage;
  const loadMore = () => {
    if (more && !search.isFetchingNextPage) void search.fetchNextPage();
  };

  return (
    <div>
      <PageHeader
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
        actions={
          me.person.kind === 'employee' && (
            <Button icon={<Plus className="size-4" />} onClick={() => setManual(true)}>
              {t('tasks.createManual')}
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-col gap-2 rounded-lg border border-line bg-sheet p-3">
        <div className="flex flex-wrap items-center gap-2">
          <SourceSwitch value={source} onChange={setSource} />
          <ProjectCombobox
            className="min-w-64 flex-1"
            allLabel={t('picker.allProjects')}
            value={projectId}
            onChange={setProjectId}
            projects={(projects.data?.projects ?? []).map((p) => ({
              id: p.id,
              number: p.number,
              name: p.name,
              formaName: p.formaName,
              hint: t('picker.counts', { forma: p.formaTasks, manual: p.manualTasks }),
            }))}
          />
          {source !== 'manual' && <FormaKindSelect className="w-full sm:w-64" value={kind} onChange={setKind} projectId={projectId} />}
          <div className="relative min-w-64 flex-1">
            <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-faint" />
            <Input className="pl-8" placeholder={t('tasks.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showLocations && locations.data && (
            <LocationSelects
              nodes={locations.data.nodes}
              top={top}
              sub={sub}
              onChange={(a, b) => {
                setTop(a);
                setSub(b);
              }}
            />
          )}
          {source === 'manual' && (
            <Select className="w-64" value={category} onChange={(e) => setCategory(e.target.value)} aria-label={t('tasks.category')}>
              <option value="">{t('picker.allCategories')}</option>
              {cats.list.map((c) => (
                <option key={c.key} value={c.key}>
                  {cats.label(c.key)}
                </option>
              ))}
            </Select>
          )}
          <Checkbox label={t('tasks.includeClosed')} checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} />
          <span className="ml-auto text-sm text-graphite">
            {t('tasks.count', { count: rows.length })}
            {more ? '+' : ''}
          </span>
        </div>
      </div>
      {search.isLoading && <Loading />}
      <ErrorBox error={search.error} />
      {search.data && rows.length === 0 && <EmptyState>{t('tasks.empty')}</EmptyState>}
      {rows.length > 0 && (
        <VirtualTable
          rows={rows}
          rowKey={(x) => x.id}
          rowHeight={56}
          fixed
          minWidth={900}
          label={t('tasks.title')}
          maxHeight="min(70vh, 48rem)"
          resetKey={JSON.stringify(filters)}
          onNearEnd={loadMore}
          head={
            <tr>
              <th
                className={th}
                aria-sort={order.sort && ['ref', 'task'].includes(order.sort.key) ? (order.sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <span className="inline-flex items-center gap-3">
                  <SortButton label={t('tasks.sortNumber')} sortKey="ref" sort={order.sort} onSort={order.toggle} />
                  <SortButton label={t('common.task')} sortKey="task" sort={order.sort} onSort={order.toggle} />
                </span>
              </th>
              <SortTh className="w-64" label={t('common.project')} sortKey="project" sort={order.sort} onSort={order.toggle} />
              <SortTh className="w-36" label={t('tasks.billingColumn')} sortKey="billing" sort={order.sort} onSort={order.toggle} />
              <th className={clsx(th, 'w-48')} />
            </tr>
          }
          renderRow={(x) => {
            const link = formaLink(me.settings.deepLinkTemplate, x.formaProjectId, x.formaIssueId);
            const picked = toPicked(x);
            return (
              <>
                <td className={cell}>
                  <div className="flex h-5 items-center gap-1.5 overflow-hidden text-sm">
                    <span className="num shrink-0 font-semibold text-accent">{x.ref}</span>
                    <span className="truncate font-medium" title={x.title}>
                      {x.title}
                    </span>
                    <TaskBadges formaState={x.formaState} statusGroup={x.statusGroup} source={x.source} />
                  </div>
                  <div className="h-4 overflow-hidden whitespace-nowrap">
                    <TaskMeta task={picked} showProject={false} />
                  </div>
                </td>
                <td className={cell}>
                  <span className={clsx(clip, 'text-sm')} title={`${x.projectNumber} ${x.projectName}`}>
                    <span className="num font-semibold">{x.projectNumber}</span> {x.projectName}
                  </span>
                  {x.formaProjectName && x.formaProjectName !== x.projectName && (
                    <span className={clsx(clip, 'text-xs text-graphite')} title={x.formaProjectName}>
                      Forma: {x.formaProjectName}
                    </span>
                  )}
                </td>
                <td className={cell}>
                  <span className={clsx(clip, 'text-xs text-graphite')}>{t(`billingType.${x.defaultBillingType}`)}</span>
                </td>
                <td className={clsx(cell, 'text-right')}>
                  <span className="inline-flex items-center gap-2">
                    {link && (
                      <FormaIssueLink href={link} title={t('task.openInForma')} className="inline-flex size-7 items-center justify-center rounded text-graphite hover:bg-sheet-2 hover:text-accent">
                        <ExternalLink className="size-4" aria-hidden />
                        <span className="sr-only">{t('task.openInForma')}</span>
                      </FormaIssueLink>
                    )}
                    <FavoriteStar on={favs.ids.has(x.id)} disabled={favs.pending} onToggle={() => favs.toggle(x.id)} />
                    <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setBook(picked)}>
                      {t('tasks.book')}
                    </Button>
                  </span>
                </td>
              </>
            );
          }}
        />
      )}
      {search.isFetchingNextPage && <p className="mt-2 text-xs text-graphite">{t('app.loading')}</p>}
      <EntryDialog open={!!book} onClose={() => setBook(null)} defaultTask={book} defaultDate={me.today} />
      <ManualTaskDialog open={manual} onClose={() => setManual(false)} />
    </div>
  );
}
